import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	Observable,
	of,
	ReplaySubject,
	takeUntil,
	tap,
	timer,
} from 'rxjs';

import {
	checkStringEquality,
	DcDxGrids,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from '../../../core/dx-types';
import {
	DxListsStorage,
	IDxList,
} from '../../../core/services/dx-lists.storage';
import { goToHdHome, goToProjects } from '../../../core/store';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { HdIcons } from '../../../ui/app-hd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	DashboardSelector,
	initDashboardList,
	navigateToDashboard,
	navigateToDashboardCreate,
	navigateToDashboardsCarouselRoute,
	openDeleteConfirmationPopup,
	refreshDashboardList,
	toggleDashboardStatus,
} from '../../store';
import { DashboardItemEntity } from './dashboard-item.entity';
import { DashboardListEntity } from './dashboard-list.entity';

export type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

@Component({
	selector: 'app-dashboard-list',
	templateUrl: './dashboard-list.component.html',
	styleUrls: ['./dashboard-list.component.scss'],
	inputs: ['projectLabel'],
})
export class DashboardListComponent extends DcBaseComponent implements OnInit {
	protected readonly DcIcons = DcIcons;
	protected readonly HdIcons = HdIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public paths: Array<IBreadCrumbEntry> = [];
	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;
	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const depot = item as DashboardItemEntity;
				this.toggleActivationStatus(depot);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];
	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const depot = item as DashboardItemEntity;
				this.toggleActivationStatus(depot);
			},
			icon: ActionIcons.ActivateElement,
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];

	public vo$!: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: DashboardListEntity;
	}>;

	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly dashListSubject = new ReplaySubject<DashboardListEntity>(1);

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	public constructor(
		private readonly store: Store,
		private readonly dashboardSelector: DashboardSelector,
		private readonly dxStorage: DxListsStorage,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'dashboard-list';
		this.dxLocalStorageKey = DcDxGrids.DashboardListStandalone;

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.dashboardSelector.getLoading$(),
			list: this.dashListSubject,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.triggerInit();
		this.listenToEvents();
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshDashboardList());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}
	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public createNewDashboard(): void {
		this.store.dispatch(navigateToDashboardCreate());
	}

	public navigateToDashboard(
		dashboard: DashboardItemEntity,
		goToVisuTab = false
	): void {
		this.store.dispatch(
			navigateToDashboard({
				id: dashboard.id,
				goToVisuTab,
			})
		);
	}

	public navigateToCarouselView(): void {
		this.store.dispatch(navigateToDashboardsCarouselRoute());
	}

	public deleteDashboard(event: Event, dashboard: DashboardItemEntity): void {
		event.stopPropagation();
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				dashboardId: [dashboard.id],
				dashboardLabel: dashboard.label,
			})
		);
	}

	public toggleActivationStatus(dashboard: DashboardItemEntity): void {
		this.store.dispatch(
			toggleDashboardStatus({
				dashboardId: dashboard.id,
				status: !dashboard.isActive,
			})
		);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: DashboardItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();
			if (tags.length === 0 && value === NoopValue) {
				return true;
			}
			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: DashboardItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	private triggerInit(): void {
		this.store.dispatch(initDashboardList());

		combineAll({
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToHdHome()),
						},
						{
							label: $localize`:i18n=@@dashboard.list.title:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.dashboardSelector
			.getDashboardList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();

					this.dashListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();
	}

	private listenToEvents(): void {
		this.dashboardSelector
			.getSuccessDeleteDashboards$()
			.pipe(
				takeUntil(this.onDestroy$),
				tap((success) => {
					if (success) {
						this.grid?.instance.clearSelection();
						this.grid?.instance.deselectAll();
					}
				})
			)
			.subscribe();
	}

	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedDashboards = await firstValueFrom(
			this.dashboardSelector.getSelectedDashboards$(selectedRowKeys)
		);
		if (selectedDashboards.length === 1) {
			return this.store.dispatch(
				openDeleteConfirmationPopup({
					dashboardId: [selectedDashboards[0].id],
					dashboardLabel: selectedDashboards[0].label,
				})
			);
		}
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				dashboardId: selectedDashboards.map((k) => k.id),
				dashboardLabel: '',
			})
		);
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@dashboard.list.columnSelection:`,
				typed: '',
			},
			{
				order: 2,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@dashboard.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}
}
